import { useContext, type ReactNode } from 'react';
import { MeContext } from './MeContext';
import { ASK_THE_SUPERADMIN, plainWords, technical } from '@/api/plain';
import { toolWords, type ToolKind } from '@norum/contracts';
import { Icon, type Glyph } from '@/ui/icons';

export { ASK_THE_SUPERADMIN, plainWords, technical };

/**
 * Technical detail is the superadmin's (ADR-0140, "Technical detail is the
 * superadmin's"). Everyone else is shown what they work on: a draft, a
 * request, a record, a plan in words. The ids an agent resolves, the codes
 * a gateway gives, a tool's id, a model's region, the section of the
 * constitution a rule comes from — the superadmin reads those, because they
 * are the one who can act on them.
 *
 * Fail closed: with no signed-in person in view, nothing technical shows.
 */
export function useTechnical(): boolean {
  return useContext(MeContext)?.isSuperadmin === true;
}

/** Its children for a superadmin; nothing for anyone else. */
export function Technical({ children }: { children: ReactNode }) {
  return useTechnical() ? <>{children}</> : null;
}

/** The words as this reader should see them: plain for everyone but a superadmin. */
export function useWords(): (text: string) => string {
  return useTechnical() ? (t) => t : plainWords;
}

// Every tool by what it does, from the one list that names them all
// (packages/contracts/src/tool-words.ts), with the icon of its kind.
const KIND_GLYPH: Record<ToolKind, Glyph> = {
  search: 'search',
  place: 'map',
  page: 'page',
  mail: 'mail',
  calendar: 'calendar',
  drive: 'folder',
  chat: 'ask',
  register: 'register',
  data: 'reports',
  model: 'model',
  people: 'people',
  check: 'check',
  lifecycle: 'people',
  notice: 'bell',
};

export function toolName(id: string): string {
  return toolWords(id).name;
}

/** What a tool is for, in one sentence; empty for one the list does not name. */
export function toolPurpose(id: string): string {
  return toolWords(id).purpose;
}

export function toolGlyph(id: string): Glyph {
  return KIND_GLYPH[toolWords(id).kind];
}

/** A tool by what it does, with its icon; what it is for on hover; its id beside it for the superadmin. */
export function ToolLabel({ id, icon = true }: { id: string; icon?: boolean }) {
  const tech = useTechnical();
  const w = toolWords(id);
  return (
    <span className="inline-flex items-center gap-1.5" title={w.purpose || undefined}>
      {icon && <span className="text-meta"><Icon glyph={KIND_GLYPH[w.kind]} size={13} /></span>}
      <span>{w.name}</span>
      {tech && <span className="font-mono text-label text-meta">{id}</span>}
    </span>
  );
}

/** Several tools, in words, for text where a component cannot go. */
export function useToolNames(): (ids: readonly string[]) => string {
  const tech = useTechnical();
  return (ids) => ids.map((id) => (tech ? `${toolName(id)} (${id})` : toolName(id))).join(', ');
}

/** What the scanner said, in words a person reads. */
export function scanWords(verdict: string): string {
  return verdict === 'blocked' ? 'Held back as unsafe' : verdict === 'flagged' ? 'Looks suspicious' : verdict === 'pending' ? 'Being checked' : verdict;
}

/**
 * A node's words without citations, for a reader who is no superadmin: a
 * string, or the strings among a node's children (JSX with an interpolation
 * is an array). Anything else passes as it is.
 */
export function usePlain(): <T>(node: T) => T {
  const tech = useTechnical();
  if (tech) return (node) => node;
  const plain = (node: unknown): unknown =>
    typeof node === 'string' ? plainWords(node) : Array.isArray(node) ? node.map(plain) : node;
  return plain as <T>(node: T) => T;
}

/** Its text without the rulebook's citations, unless a superadmin reads it. */
export function Plain({ children }: { children: ReactNode }) {
  return <>{usePlain()(children)}</>;
}
